import { useStyletron } from '@thanx/uikit/theme'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import { buildTranslate } from 'locales'
import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'
import CmsPage from 'scenes/Cms/components/CmsPage'
import PublishBanner from 'scenes/Cms/components/PublishBanner'
import { useUnsavedChangesPrompt } from 'scenes/RecoveryAI/Settings/useUnsavedChangesPrompt'

import useDispatch from 'hooks/useDispatch'
import { setAllowWrittenFeedbackCollection } from 'slices/orderFeedbackCollection'
import {
  selectAllowWrittenFeedbackCollection,
  selectFormDisabled,
  selectIsFormDirty,
  selectIsFormSubmitting,
  selectPersistedAllowWrittenFeedbackCollection,
  selectPublishDisabled,
} from 'slices/orderFeedbackCollection/selectors'
import CurrentSetupCard from './CurrentSetupCard'
import { useFeedbackCollectionFormInitialize } from './hooks/useFeedbackCollectionFormInitialize'
import { useFeedbackCollectionSubmitForm } from './hooks/useFeedbackCollectionSubmitForm'
import { useSettingsEditPermission } from './hooks/useSettingsEditPermission'
import { useSetupCardState } from './hooks/useSetupCardState'
import { PANE_TITLE_STYLE } from './paneTitleStyle'
import WrittenFeedbackCard from './WrittenFeedbackCard'

const t = buildTranslate('order_ratings.settings')

const PUBLISH_PERMISSION_TOOLTIP_ID = 'feedback-collection-publish-permission'

type Props = {
  // AI-1282: the Settings container now owns ONE Publish bar for the whole
  // tab, because this pane's toggle is visible above every sub-tab and a
  // shared Publish has to catch it regardless of which sub-tab is active.
  // Defaults to true so every other mount of this pane (there is exactly one
  // today, but the flag defaults to preserving it) keeps its own bar.
  showPublishBanner?: boolean
}

// The Feedback collection sub-tab. CmsPage renders the <h3> title and the <p>
// description; the written-feedback toggle (AI-1267) and the current-setup
// card (AI-1268) sit between them and the publish bar. The toggle comes FIRST:
// the card renders nothing while collection is off, so putting it below the
// toggle means removing it moves nothing the merchant is touching mid-toggle.
const FeedbackCollectionPane = ({ showPublishBanner = true }: Props) => {
  const [css] = useStyletron()
  const dispatch = useDispatch()

  const isMerchantLoaded = useFeedbackCollectionFormInitialize()

  const allowWrittenFeedbackCollection = useSelector(
    selectAllowWrittenFeedbackCollection
  )
  const isSubmitting = useSelector(selectIsFormSubmitting)
  const isFormDirty = useSelector(selectIsFormDirty)
  const formDisabled = useSelector(selectFormDisabled)
  const publishDisabled = useSelector(selectPublishDisabled)
  // The off-state note reports what guests are getting, so like the setup card
  // it follows the SAVED value: it appears when Publish turns collection off,
  // and clears when Publish turns it back on.
  const persistedCollecting = useSelector(
    selectPersistedAllowWrittenFeedbackCollection
  )

  // Warn before leaving the route (in-app nav + refresh/close) with an
  // unpublished edit, matching the RecoveryAI Settings panes. The outer Order
  // feedback tabs unmount this pane on a tab switch, which drops the edit with
  // no prompt otherwise.
  const unsavedChangesPrompt = useUnsavedChangesPrompt(isFormDirty)

  // FR-60. RBAC sits ON TOP of the two slice gates, never in place of either:
  // `formDisabled` still holds for an in-flight save, and `publishDisabled`
  // still holds for a clean form, for a viewer who HAS the permission.
  const { editDisabled, permissionReason } = useSettingsEditPermission()

  // FR-22's card. Read-only, so no RBAC gate: a viewer without
  // `manage MerchantSettings` sees the same card an editor does. The state is
  // resolved in setupCardState.ts (AI-1257 replaces that module); nothing
  // about the states is decided here or in the card's JSX.
  //
  // `null` while the hook's inputs are still in flight — the card would
  // otherwise name the wrong setup for a round trip or two and then rewrite
  // itself. Nothing renders in that window.
  const setupCardState = useSetupCardState()

  const handleSubmit = useFeedbackCollectionSubmitForm()
  const handleToggleChange = useCallback(
    (checked: boolean) => {
      dispatch(setAllowWrittenFeedbackCollection(checked))
    },
    [dispatch]
  )

  return (
    <CmsPage
      title={t('feedback_collection.title')}
      titleClassName={css(PANE_TITLE_STYLE)}
      description={t('feedback_collection.description')}
    >
      {/* Guard whole-route navigation (in-app + refresh/close) while dirty. */}
      {unsavedChangesPrompt}
      <WrittenFeedbackCard
        checked={allowWrittenFeedbackCollection}
        // Three independent reasons to disable, all of which must hold off a
        // click: an in-flight save, a merchant GET that has not landed (the
        // slice seeds from a merchant that is not there yet, so a click in
        // that window edits a value the pane is about to overwrite), and a
        // viewer without the permission.
        disabled={formDisabled || !isMerchantLoaded || editDisabled}
        // Keyed to the PERMISSION alone, not to the combined `disabled` above:
        // a permitted merchant mid-save, or one waiting on the merchant GET,
        // would otherwise be handed a tooltip claiming they lack the permission.
        permissionReason={permissionReason}
        showOffNote={!persistedCollecting}
        onChange={handleToggleChange}
      />
      {/* Renders nothing at all while written feedback is off, and nothing
          while the setup is still resolving. */}
      {setupCardState && <CurrentSetupCard state={setupCardState} />}
      {/* Publish is dirty-gated: disabled until the toggle differs from the
          persisted value, and again while the save is in flight. The CMS
          Settings precedent leaves it always enabled, which makes it a dead
          control on every pane load; Live Updates tracks dirty state and this
          pane follows that. RBAC is a THIRD condition on top, not a
          replacement — a permitted viewer still gets a disabled Publish while
          the form is clean. `buttonType="button"` because this pane is not a
          <form>: the handler is wired directly, not through a formless submit. */}
      {/* Same treatment the toggle gets, and the same one AlertBannerSettings'
          PublishBar gives its own Publish: a greyed control that says nothing
          on hover is a dead end. Wrapped around the whole banner rather than
          the Button alone because PublishBanner is a shared CMS component that
          renders its own Button and takes no slot for one. Keyed to the
          PERMISSION alone, so an in-flight save is never explained as a
          permissions problem. Block (not `inline`) so the full-width banner
          keeps its width. */}
      {showPublishBanner && (
        <PermissionTooltip
          disabled={Boolean(permissionReason)}
          reason={permissionReason}
          id={PUBLISH_PERMISSION_TOOLTIP_ID}
          placement="left"
        >
          <PublishBanner
            buttonType="button"
            buttonText={t('publish')}
            isLoading={isSubmitting}
            disabled={publishDisabled || editDisabled}
            onClick={handleSubmit}
          />
        </PermissionTooltip>
      )}
    </CmsPage>
  )
}

export default FeedbackCollectionPane
